import type React from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  fetchMe,
  login as apiLogin,
  logout as apiLogout,
  changePassword as apiChangePassword,
  hasSession,
  clearSession,
  type AdminProfile,
} from '../api/session';

interface AuthContextValue {
  admin: AdminProfile | null;
  /** True until the initial session probe finishes. */
  initializing: boolean;
  isSuperAdmin: boolean;
  login: (username: string, password: string) => Promise<AdminProfile>;
  logout: () => Promise<void>;
  changePassword: (current: string, next: string) => Promise<AdminProfile>;
  /** Called when a request discovers the session is dead. */
  onSessionExpired: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [admin, setAdmin] = useState<AdminProfile | null>(null);
  const [initializing, setInitializing] = useState(true);

  // On boot the access token is gone (memory-only) but the refresh token may
  // still be in sessionStorage, so try to restore the session before deciding
  // the user is logged out.
  useEffect(() => {
    let cancelled = false;
    if (!hasSession()) {
      setInitializing(false);
      return;
    }
    fetchMe()
      .then((profile) => {
        if (!cancelled) setAdmin(profile);
      })
      .catch(() => {
        if (!cancelled) {
          clearSession();
          setAdmin(null);
        }
      })
      .finally(() => {
        if (!cancelled) setInitializing(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const result = await apiLogin(username, password);
    setAdmin(result.admin);
    return result.admin;
  }, []);

  const logout = useCallback(async () => {
    await apiLogout();
    setAdmin(null);
  }, []);

  const changePassword = useCallback(async (current: string, next: string) => {
    const updated = await apiChangePassword(current, next);
    setAdmin(updated);
    return updated;
  }, []);

  const onSessionExpired = useCallback(() => {
    clearSession();
    setAdmin(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      admin,
      initializing,
      isSuperAdmin: admin?.role === 'super_admin',
      login,
      logout,
      changePassword,
      onSessionExpired,
    }),
    [admin, initializing, login, logout, changePassword, onSessionExpired],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth deve ser usado dentro de <AuthProvider>');
  return ctx;
}
